import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { stakeholdersAPI, invitationsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Users,
  Mail,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  Search,
  UserCheck,
  UserX,
  Download,
  Upload,
  Shield,
  ShieldOff,
  Crown,
  Globe,
  Copy,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  Filter,
  Settings,
  CalendarDays
} from 'lucide-react';
import PublicStakeholdersSection from '@/components/stakeholders/PublicStakeholdersSection';
import InvitedUsersSection from '@/components/stakeholders/InvitedUsersSection';
import AddStakeholderForm from '@/components/stakeholders/AddStakeholderForm';
import { EmailConfigPanel } from '@/components/EmailConfigPanel';

const statusColors = {
  active: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Invited' },
  invited: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Invited' },
  pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
  reference_only: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Reference' },
  reference: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Reference' },
};

const inviteStatusColors = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  accepted: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  expired: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
};

// Memoized table row to prevent full-table re-render on checkbox toggle
const StakeholderRow = React.memo(function StakeholderRow({
  stakeholder,
  segmentTypes,
  segments,
  segmentMap,
  isSelected,
  isInvitable,
  onToggleSelect,
  onEdit,
  onDelete,
  onInlineUpdate,
  onApprove,
  onReject,
  onResendInvite,
  onPromoteAdmin,
  onDemoteAdmin,
  resendingInvite,
}) {
  const status = statusColors[stakeholder.status] || statusColors.active;

  // Build segment map for this stakeholder
  const stakeholderSegments = {};
  (stakeholder.segment_assignments || []).forEach(sa => {
    const seg = segmentMap[sa.segment_id];
    if (seg) {
      stakeholderSegments[sa.segment_type_id] = seg;
    }
  });

  // Format invitation display
  const invitationDisplay = stakeholder.invitation_sent_at
    ? new Date(stakeholder.invitation_sent_at).toLocaleDateString()
    : 'Not Sent';

  // Format last login display
  const loginDisplay = stakeholder.days_since_login !== null && stakeholder.days_since_login !== undefined
    ? `${stakeholder.days_since_login}d`
    : 'Never';
  const loginColor = stakeholder.days_since_login !== null && stakeholder.days_since_login !== undefined
    ? (stakeholder.days_since_login > 14 ? 'text-red-500' : stakeholder.days_since_login > 7 ? 'text-amber-600' : 'text-slate-600')
    : 'text-slate-400';

  return (
    <TableRow data-testid={`stakeholder-row-${stakeholder.id}`}>
      <TableCell>
        {isInvitable && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(stakeholder.id)}
            className="rounded border-slate-300"
          />
        )}
      </TableCell>
      <TableCell className="font-medium">
        <span>{stakeholder.first_name} {stakeholder.last_name}</span>
      </TableCell>
      {/* Status - Inline editable */}
      <TableCell className="p-1">
        <Select
          value={stakeholder.status || 'active'}
          onValueChange={(value) => onInlineUpdate(stakeholder.id, 'status', value)}
        >
          <SelectTrigger className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent w-24">
            <SelectValue>
              <Badge className={`${status.bg} ${status.text} text-xs`}>
                {status.label}
              </Badge>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Invited</Badge>
            </SelectItem>
            <SelectItem value="reference_only">
              <Badge className="bg-slate-100 text-slate-700 text-xs">Reference</Badge>
            </SelectItem>
            <SelectItem value="pending_approval">
              <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>
            </SelectItem>
            <SelectItem value="rejected">
              <Badge className="bg-red-100 text-red-700 text-xs">Rejected</Badge>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {stakeholder.is_account_owner ? (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
              <Crown className="w-3 h-3 mr-1" />
              Owner
            </Badge>
          ) : stakeholder.is_admin ? (
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
              <Shield className="w-3 h-3 mr-1" />
              Admin
            </Badge>
          ) : (
            <span className="text-slate-400 text-sm">-</span>
          )}
        </div>
      </TableCell>
      {/* Dynamic cells for each Segment Type - Inline editable */}
      {segmentTypes.map((st) => {
        const segmentsForType = segments.filter(s => s.segment_type_id === st.id);
        const currentSegment = stakeholderSegments[st.id];
        
        return (
          <TableCell key={st.id} className="text-sm p-1">
            <Select
              value={currentSegment?.id || 'none'}
              onValueChange={(value) => onInlineUpdate(stakeholder.id, `segment_${st.id}`, value)}
            >
              <SelectTrigger className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent">
                <SelectValue>
                  {currentSegment ? (
                    <span className="px-2 py-0.5 bg-slate-100 rounded text-xs">
                      {currentSegment.name}
                    </span>
                  ) : (
                    <span className="text-slate-300">-</span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-slate-400">None</span>
                </SelectItem>
                {segmentsForType.map(seg => (
                  <SelectItem key={seg.id} value={seg.id}>
                    {seg.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TableCell>
        );
      })}
      {/* Source */}
      <TableCell>
        {stakeholder.source ? (
          <span className="text-xs text-slate-500 capitalize">
            {stakeholder.source === 'admin_added' ? 'Admin' : 
             stakeholder.source === 'user_identified' ? 'User' : 
             stakeholder.source === 'self_identified' ? 'Self' :
             stakeholder.source === 'verified' ? 'Verified' : 
             stakeholder.source.replace('_', ' ')}
          </span>
        ) : (
          <span className="text-slate-300 text-xs">-</span>
        )}
      </TableCell>
      {/* Invitation column - date or "Not Sent" */}
      <TableCell>
        {stakeholder.invitation_sent_at ? (
          <span className="text-xs text-slate-600">{invitationDisplay}</span>
        ) : (
          <span className="text-xs text-slate-400 italic">Not Sent</span>
        )}
      </TableCell>
      {/* Last Login column */}
      <TableCell>
        <span className={`text-xs ${loginColor}`}>{loginDisplay}</span>
      </TableCell>
      {/* Actions */}
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid={`stakeholder-actions-${stakeholder.id}`}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(stakeholder)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            {stakeholder.email && (
              <DropdownMenuItem 
                onClick={() => onResendInvite(stakeholder)}
                disabled={resendingInvite === stakeholder.id}
              >
                <Send className="mr-2 h-4 w-4" />
                {resendingInvite === stakeholder.id ? 'Sending...' : 'Resend Invitation'}
              </DropdownMenuItem>
            )}
            {!stakeholder.is_account_owner && (
              stakeholder.is_admin ? (
                <DropdownMenuItem onClick={() => onDemoteAdmin(stakeholder.id)}>
                  <ShieldOff className="mr-2 h-4 w-4" />
                  Remove Admin
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onPromoteAdmin(stakeholder.id)}>
                  <Shield className="mr-2 h-4 w-4" />
                  Make Admin
                </DropdownMenuItem>
              )
            )}
            {!stakeholder.is_account_owner && (
              <DropdownMenuItem 
                className="text-red-600"
                onClick={() => onDelete(stakeholder.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
});

export default function StakeholdersPage() {
  const { projectId } = useParams();
  const { currentProject, segmentTypes, segments, selectProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [stakeholders, setStakeholders] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Pagination state
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'all';
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);
  
  // Sort state
  const [sortBy, setSortBy] = useState('role_name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Column filter state - filters by segment type
  const [columnFilters, setColumnFilters] = useState({});
  
  // Status filter state (server-side)
  const [statusFilter, setStatusFilter] = useState('');
  
  // Update column filter
  const updateColumnFilter = (segmentTypeId, segmentId) => {
    setColumnFilters(prev => {
      if (!segmentId || segmentId === 'all') {
        // Remove filter
        const newFilters = { ...prev };
        delete newFilters[segmentTypeId];
        return newFilters;
      }
      return { ...prev, [segmentTypeId]: segmentId };
    });
    setCurrentPage(1); // Reset to first page
  };
  
  // Clear all column filters
  const clearAllFilters = () => {
    setColumnFilters({});
    setStatusFilter('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [stakeholdersRes, invitationsRes] = await Promise.all([
        stakeholdersAPI.list(projectId, {
          page: currentPage,
          page_size: pageSize,
          search: debouncedSearch || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          status_filter: statusFilter || undefined,
        }),
        invitationsAPI.list(projectId),
      ]);
      
      // Handle paginated response
      const data = stakeholdersRes.data;
      if (data.stakeholders) {
        setStakeholders(data.stakeholders);
        setTotalPages(data.total_pages || 1);
        setTotalCount(data.total || 0);
      } else {
        // Fallback for old API format
        setStakeholders(data);
        setTotalCount(data.length);
      }
      setInvitations(invitationsRes.data);
    } catch (err) {
      console.error('Failed to fetch stakeholders:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, currentPage, pageSize, debouncedSearch, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    const load = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await selectProject(projectId);
      }
    };
    load();
  }, [projectId, currentProject, selectProject]);
  
  // Separate effect for fetching data when pagination/sort/search changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter out Anonymous and Rejected from view (server already sorts)
  const filteredStakeholders = useMemo(() => {
    return stakeholders.filter(s => {
      const isAnonymous = !s.first_name || 
        s.first_name.toLowerCase().includes('anonymous') ||
        s.source === 'anonymous';
      const isRejected = s.status === 'rejected';
      
      if (isAnonymous || isRejected) return false;
      
      // Apply column filters
      for (const [segmentTypeId, segmentId] of Object.entries(columnFilters)) {
        const assignments = s.segment_assignments || [];
        const hasSegment = assignments.some(
          a => a.segment_type_id === segmentTypeId && a.segment_id === segmentId
        );
        if (!hasSegment) return false;
      }
      
      return true;
    });
  }, [stakeholders, columnFilters]);

  // Handle column sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const resetForm = () => {
    setEditingStakeholder(null);
  };

  // Handler for the memoized AddStakeholderForm component
  const handleFormSubmit = async (formData, editingStakeholder) => {
    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email || null,
        project_title: formData.project_title || null,
        segment_assignments: formData.segment_assignments,
        is_reference_only: !formData.invite_to_pulse,
      };

      if (editingStakeholder) {
        await stakeholdersAPI.update(projectId, editingStakeholder.id, payload);
        toast.success('Stakeholder updated');
      } else {
        await stakeholdersAPI.create(projectId, payload);
        toast.success('Stakeholder created');
      }
      await fetchData();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save stakeholder');
      throw err; // Re-throw so the form knows it failed
    }
  };

  const handleEdit = (stakeholder) => {
    setEditingStakeholder(stakeholder);
    setDialogOpen(true);
  };

  const handleDelete = async (stakeholderId) => {
    if (!window.confirm('Delete this stakeholder?')) return;
    
    try {
      await stakeholdersAPI.delete(projectId, stakeholderId);
      toast.success('Stakeholder deleted');
      await fetchData();
    } catch (err) {
      toast.error('Failed to delete stakeholder');
    }
  };

  // Resend invitation to a specific stakeholder from the grid
  const [resendingInvite, setResendingInvite] = useState(null);
  
  const handleResendStakeholderInvitation = async (stakeholder) => {
    if (!stakeholder.email) {
      toast.error('Stakeholder has no email address');
      return;
    }
    
    if (resendingInvite === stakeholder.id) {
      return; // Prevent double-click
    }
    
    setResendingInvite(stakeholder.id);
    const token = localStorage.getItem('pulse_token');
    
    if (!token) {
      toast.error('Please log in again');
      setResendingInvite(null);
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/stakeholders/${stakeholder.id}/resend-invitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to resend invitation';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Use default error message
        }
        
        if (response.status === 401) {
          toast.error('Session expired. Please log in again.');
        } else {
          toast.error(errorMessage);
        }
        return;
      }
      
      toast.success(`Invitation re-sent to ${stakeholder.email}`);
    } catch (err) {
      console.error('Resend invitation error:', err);
      toast.error('Network error. Please try again.');
    } finally {
      setResendingInvite(null);
    }
  };

  // Inline update for segment or status
  const handleInlineUpdate = async (stakeholderId, field, value) => {
    try {
      const stakeholder = stakeholders.find(s => s.id === stakeholderId);
      if (!stakeholder) return;

      let payload = {};
      let optimisticUpdate = {};
      
      if (field === 'status') {
        payload.status = value;
        optimisticUpdate = { status: value };
      } else if (field.startsWith('segment_')) {
        // field is like 'segment_TYPE_ID' - extract segment type ID
        const segmentTypeId = field.replace('segment_', '');
        
        // Build new segment_assignments array
        const currentAssignments = stakeholder.segment_assignments || [];
        const otherAssignments = currentAssignments.filter(sa => sa.segment_type_id !== segmentTypeId);
        
        if (value && value !== 'none') {
          payload.segment_assignments = [
            ...otherAssignments,
            { segment_type_id: segmentTypeId, segment_id: value }
          ];
        } else {
          // Remove segment assignment for this type
          payload.segment_assignments = otherAssignments;
        }
        optimisticUpdate = { segment_assignments: payload.segment_assignments };
      }

      // Optimistic update - update local state immediately
      setStakeholders(prev => prev.map(s => 
        s.id === stakeholderId ? { ...s, ...optimisticUpdate } : s
      ));

      // Make API call in background
      await stakeholdersAPI.update(projectId, stakeholderId, payload);
      toast.success('Updated successfully');
    } catch (err) {
      toast.error('Failed to update');
      // Revert on error by refetching
      await fetchData();
    }
  };

  const handleSendInvitation = async (stakeholderId) => {
    try {
      await invitationsAPI.create(projectId, stakeholderId);
      toast.success('Invitation sent');
      await fetchData();
    } catch (err) {
      toast.error('Failed to send invitation');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await invitationsAPI.resend(projectId, invitationId);
      toast.success('Invitation re-sent');
      await fetchData();
    } catch (err) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleBulkInvite = async () => {
    if (selectedForBulk.length === 0) {
      toast.error('Select stakeholders first');
      return;
    }
    
    try {
      await invitationsAPI.bulk(projectId, selectedForBulk);
      toast.success(`Sent ${selectedForBulk.length} invitations`);
      setSelectedForBulk([]);
      await fetchData();
    } catch (err) {
      toast.error('Failed to send invitations');
    }
  };

  const handleApprove = async (stakeholderId) => {
    try {
      await stakeholdersAPI.approve(projectId, stakeholderId);
      toast.success('Stakeholder approved');
      await fetchData();
    } catch (err) {
      toast.error('Failed to approve stakeholder');
    }
  };

  const handleReject = async (stakeholderId) => {
    try {
      await stakeholdersAPI.reject(projectId, stakeholderId);
      toast.success('Stakeholder rejected');
      await fetchData();
    } catch (err) {
      toast.error('Failed to reject stakeholder');
    }
  };

  const [exportUrl, setExportUrl] = useState(null);

  const handleExport = async () => {
    // Get the token for authentication
    const token = localStorage.getItem('pulse_token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const url = `${API_URL}/api/projects/${projectId}/stakeholders/export?token=${encodeURIComponent(token)}`;
    
    // Set the URL to show in dialog
    setExportUrl(url);
  };

  const handleCopyExportUrl = () => {
    if (exportUrl) {
      navigator.clipboard.writeText(exportUrl);
      toast.success('Link copied! Paste in a new browser tab to download.');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const response = await stakeholdersAPI.import(projectId, file);
      const { created, updated, errors } = response.data;
      
      if (errors && errors.length > 0) {
        toast.warning(`Import completed with errors: ${created} created, ${updated} updated`);
      } else {
        toast.success(`Import successful: ${created} created, ${updated} updated`);
      }
      
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import stakeholders');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePromoteAdmin = async (stakeholderId) => {
    try {
      await stakeholdersAPI.promoteAdmin(projectId, stakeholderId);
      toast.success('Stakeholder promoted to admin');
      await fetchData();
    } catch (err) {
      toast.error('Failed to promote stakeholder');
    }
  };

  const handleDemoteAdmin = async (stakeholderId) => {
    try {
      await stakeholdersAPI.demoteAdmin(projectId, stakeholderId);
      toast.success('Admin status removed');
      await fetchData();
    } catch (err) {
      toast.error('Failed to remove admin status');
    }
  };

  const toggleBulkSelect = useCallback((stakeholderId) => {
    setSelectedForBulk(prev => 
      prev.includes(stakeholderId)
        ? prev.filter(id => id !== stakeholderId)
        : [...prev, stakeholderId]
    );
  }, []);

  // Memoize segment and segment type maps for O(1) lookups
  const segmentMap = useMemo(() => {
    const map = {};
    segments.forEach(s => { map[s.id] = s; });
    return map;
  }, [segments]);

  const segmentTypeMap = useMemo(() => {
    const map = {};
    segmentTypes.forEach(st => { map[st.id] = st; });
    return map;
  }, [segmentTypes]);

  const getSegmentDisplay = useCallback((stakeholder) => {
    if (!stakeholder.segment_assignments || stakeholder.segment_assignments.length === 0) {
      return '-';
    }
    
    return stakeholder.segment_assignments.map(sa => {
      const segment = segmentMap[sa.segment_id];
      const segmentType = segmentTypeMap[sa.segment_type_id];
      if (segment && segmentType) {
        return `${segmentType.name}: ${segment.name}`;
      }
      return null;
    }).filter(Boolean).join(', ');
  }, [segmentMap, segmentTypeMap]);

  // Memoize invitation status map for O(1) lookups
  const invitationStatusMap = useMemo(() => {
    const map = {};
    invitations.forEach(inv => {
      map[inv.stakeholder_id] = inv.status;
    });
    return map;
  }, [invitations]);

  const getInvitationStatus = useCallback((stakeholderId) => {
    return invitationStatusMap[stakeholderId] || null;
  }, [invitationStatusMap]);

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Memoize filtered stakeholder lists
  const pendingApprovals = useMemo(() => 
    stakeholders.filter(s => s.status === 'pending_approval'),
    [stakeholders]
  );
  
  const invitableStakeholders = useMemo(() => 
    stakeholders.filter(s => s.status === 'active' && !s.is_reference_only && s.email),
    [stakeholders]
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImport}
        accept=".xlsx,.xls"
        className="hidden"
        data-testid="import-file-input"
      />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Stakeholders
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage stakeholders and send survey invitations</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Export Button */}
          <Button 
            variant="outline"
            size="sm"
            onClick={handleExport}
            data-testid="export-stakeholders-btn"
            className="h-9"
          >
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>

          {/* Export URL Dialog */}
          <Dialog open={!!exportUrl} onOpenChange={(open) => !open && setExportUrl(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Download Export File
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-sm text-slate-600">
                  Due to browser security restrictions in this preview environment, please use one of these options to download your file:
                </p>
                <div className="space-y-3">
                  <Button 
                    className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    onClick={handleCopyExportUrl}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Download Link
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    Paste the link in a new browser tab to download
                  </p>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">or</span>
                    </div>
                  </div>
                  <a 
                    href={exportUrl || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 text-sm"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open in New Tab
                  </a>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Upload Button */}
          <Button 
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="upload-stakeholders-btn"
            className="h-9"
          >
            <Upload className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{importing ? 'Uploading...' : 'Upload'}</span>
          </Button>
          
          {/* Add Stakeholder Form - Memoized Component */}
          <AddStakeholderForm
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            segmentTypes={segmentTypes}
            segments={segments}
            editingStakeholder={editingStakeholder}
            onSubmit={handleFormSubmit}
            onOpenCreate={openCreateDialog}
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab} className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="inline-flex min-w-max">
            <TabsTrigger value="all" data-testid="tab-all-stakeholders" className="text-xs sm:text-sm">
              <Users className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">All Stakeholders</span>
              <span className="sm:hidden">All</span>
            </TabsTrigger>
            <TabsTrigger value="public" data-testid="tab-user-flow" className="text-xs sm:text-sm">
              <Globe className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">User Flow</span>
              <span className="sm:hidden">Flow</span>
            </TabsTrigger>
            <TabsTrigger value="invited" data-testid="tab-invited-users" className="text-xs sm:text-sm">
              <Send className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Invited Users</span>
              <span className="sm:hidden">Invited</span>
            </TabsTrigger>
            <TabsTrigger value="invitations" data-testid="tab-invitations" className="text-xs sm:text-sm">
              <Mail className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Invitations</span>
              <span className="sm:hidden">Sent</span>
            </TabsTrigger>
            <TabsTrigger value="email-settings" data-testid="tab-email-settings" className="text-xs sm:text-sm">
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Email Settings</span>
              <span className="sm:hidden">Email</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="all" className="space-y-4">
          {/* Search and Bulk Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search stakeholders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-stakeholders-input"
              />
            </div>
            {selectedForBulk.length > 0 && (
              <Button onClick={handleBulkInvite} data-testid="bulk-invite-btn">
                <Send className="mr-2 h-4 w-4" />
                Send {selectedForBulk.length} Invitation(s)
              </Button>
            )}
          </div>
          
          {/* Active Filters Info */}
          {(Object.keys(columnFilters).length > 0 || statusFilter) && (
            <div className="flex items-center gap-2 p-3 bg-[#5AA2AE]/10 rounded-lg border border-[#5AA2AE]/20">
              <Filter className="w-4 h-4 text-[#5AA2AE]" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">{filteredStakeholders.length}</span> stakeholders match filters:
              </span>
              <div className="flex flex-wrap gap-2">
                {statusFilter && (
                  <Badge 
                    variant="secondary" 
                    className="text-xs cursor-pointer hover:bg-slate-200"
                    onClick={() => { setStatusFilter(''); setCurrentPage(1); }}
                  >
                    Status: {(statusColors[statusFilter] || {}).label || statusFilter} &times;
                  </Badge>
                )}
                {Object.entries(columnFilters).map(([typeId, segId]) => {
                  const type = segmentTypes.find(st => st.id === typeId);
                  const seg = segments.find(s => s.id === segId);
                  return (
                    <Badge 
                      key={typeId} 
                      variant="secondary" 
                      className="text-xs cursor-pointer hover:bg-slate-200"
                      onClick={() => updateColumnFilter(typeId, null)}
                    >
                      {type?.name}: {seg?.name} &times;
                    </Badge>
                  );
                })}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto text-slate-500 hover:text-slate-700"
                onClick={clearAllFilters}
              >
                Clear All
              </Button>
            </div>
          )}

          {/* Stakeholders Table */}
          {loading ? (
            <Card className="dashboard-card">
              <Skeleton className="h-64" />
            </Card>
          ) : filteredStakeholders.length === 0 ? (
            <Card className="dashboard-card">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No stakeholders yet</h3>
                <p className="text-slate-500 mb-4">Add stakeholders to start collecting feedback</p>
                <Button 
                  className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                  onClick={openCreateDialog}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add First Stakeholder
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="dashboard-card overflow-hidden">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedForBulk.length === invitableStakeholders.length && invitableStakeholders.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedForBulk(invitableStakeholders.map(s => s.id));
                          } else {
                            setSelectedForBulk([]);
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('last_name')}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        {sortBy === 'last_name' ? (
                          sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </TableHead>
                    {/* Status column with filter */}
                    <TableHead className="min-w-[130px]">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">Status</span>
                        <Select
                          value={statusFilter || 'all'}
                          onValueChange={(val) => {
                            setStatusFilter(val === 'all' ? '' : val);
                            setCurrentPage(1);
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs" data-testid="status-filter-select">
                            <SelectValue placeholder="All" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="active">Invited</SelectItem>
                            <SelectItem value="reference_only">Reference</SelectItem>
                            <SelectItem value="pending_approval">Pending</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('role_name')}
                    >
                      <div className="flex items-center gap-1">
                        Role
                        {sortBy === 'role_name' ? (
                          sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </TableHead>
                    {/* Dynamic columns for each Segment Type with Filter */}
                    {segmentTypes.map((st) => {
                      const typeSegments = segments.filter(s => s.segment_type_id === st.id);
                      const currentFilter = columnFilters[st.id];
                      
                      return (
                        <TableHead key={st.id} className="min-w-[150px]">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">{st.name}</span>
                            <Select
                              value={currentFilter || 'all'}
                              onValueChange={(val) => updateColumnFilter(st.id, val)}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue placeholder="All" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                {typeSegments.map(seg => (
                                  <SelectItem key={seg.id} value={seg.id}>
                                    {seg.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableHead>
                      );
                    })}
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('source')}
                    >
                      <div className="flex items-center gap-1">
                        Source
                        {sortBy === 'source' ? (
                          sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : <ArrowUpDown className="w-3 h-3 text-slate-400" />}
                      </div>
                    </TableHead>
                    <TableHead>Invitation</TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        Last Login
                      </div>
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStakeholders.map((stakeholder) => {
                    const isInvitable = stakeholder.status === 'active' && !stakeholder.is_reference_only && stakeholder.email;
                    return (
                      <StakeholderRow
                        key={stakeholder.id}
                        stakeholder={stakeholder}
                        segmentTypes={segmentTypes}
                        segments={segments}
                        segmentMap={segmentMap}
                        isSelected={selectedForBulk.includes(stakeholder.id)}
                        isInvitable={isInvitable}
                        onToggleSelect={toggleBulkSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onInlineUpdate={handleInlineUpdate}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onResendInvite={handleResendStakeholderInvitation}
                        onPromoteAdmin={handlePromoteAdmin}
                        onDemoteAdmin={handleDemoteAdmin}
                        resendingInvite={resendingInvite}
                      />
                    );
                  })}
                </TableBody>
              </Table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-slate-500">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} stakeholders
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        {/* Public Stakeholders Tab */}
        <TabsContent value="public" className="space-y-4">
          <PublicStakeholdersSection 
            projectId={projectId}
            onRefreshStakeholders={fetchData}
          />
        </TabsContent>

        {/* Invited Users Tab */}
        <TabsContent value="invited" className="space-y-4">
          <InvitedUsersSection projectId={projectId} />
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          {invitations.length === 0 ? (
            <Card className="dashboard-card">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No invitations sent</h3>
                <p className="text-slate-500">Send invitations to stakeholders to collect feedback</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="dashboard-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stakeholder</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead>Expires At</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((invitation) => {
                    const stakeholder = stakeholders.find(s => s.id === invitation.stakeholder_id);
                    const status = inviteStatusColors[invitation.status];
                    const StatusIcon = status.icon;
                    
                    return (
                      <TableRow key={invitation.id}>
                        <TableCell className="font-medium">
                          {stakeholder ? `${stakeholder.first_name} ${stakeholder.last_name}` : 'Unknown'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${status.bg} ${status.text}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {invitation.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {new Date(invitation.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {new Date(invitation.expires_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {invitation.status === 'pending' && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleResendInvitation(invitation.id)}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="email-settings" className="space-y-4" data-testid="email-settings-tab-content">
          <EmailConfigPanel projectId={projectId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
